import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { ProtectedRoute } from '@shared/components/ProtectedRoute';
import { CallbackPage } from '../auth/pages/CallbackPage';
import { LandingPage } from '../auth/pages/LandingPage';
import { ResetPasswordPage } from '../auth/pages/ResetPasswordPage';
import { FeatureErrorBoundary } from './components/ErrorBoundary';

/**
 * Wrap a dynamic import with retry logic for stale chunks after deployment.
 * If the import fails (e.g., chunk hash changed), reload the page once to get fresh assets.
 */
function lazyWithRetry<T extends React.ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    importFn().catch((error: Error) => {
      // Only auto-reload for chunk load errors, and only once per session
      const isChunkError = error.name === 'ChunkLoadError' ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Loading chunk');
      const reloadKey = 'chunk-reload';

      if (isChunkError && !sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        // Return a never-resolving promise while the page reloads
        return new Promise<{ default: T }>(() => {});
      }

      // Clear the flag on successful navigation so future deploys can retry
      sessionStorage.removeItem(reloadKey);
      throw error;
    })
  );
}

// Lazy load all route components for better initial bundle size
const Dashboard = lazyWithRetry(() => import('./components/Dashboard/Dashboard'));
const Holdings = lazyWithRetry(() => import('./components/Holdings/Holdings'));
const Transactions = lazyWithRetry(() => import('./components/Transactions/Transactions'));
const Accounts = lazyWithRetry(() => import('./components/Accounts/Accounts'));
const Income = lazyWithRetry(() => import('./components/Income/Income'));
const CostBasis = lazyWithRetry(() => import('./components/CostBasis/CostBasis'));
const Gains = lazyWithRetry(() => import('./components/Gains/Gains'));
const Performance = lazyWithRetry(() => import('./components/Performance/Performance'));
const Watchlist = lazyWithRetry(() => import('./components/Watchlist/Watchlist'));
const Import = lazyWithRetry(() => import('./components/Import/Import'));
const Taxes = lazyWithRetry(() => import('./components/Taxes/Taxes'));
const Settings = lazyWithRetry(() => import('./components/Settings/Settings'));
const OptionsLab = lazyWithRetry(() => import('./components/OptionsLab/OptionsLab'));
const IncomeEstimator = lazyWithRetry(() => import('./components/IncomeEstimator/IncomeEstimator').then(m => ({ default: m.IncomeEstimator })));
const Rebalancer = lazyWithRetry(() => import('./components/Rebalancer/Rebalancer').then(m => ({ default: m.Rebalancer })));
const MarketMovers = lazyWithRetry(() => import('./components/MarketMovers/MarketMovers').then(m => ({ default: m.MarketMovers })));

// Redirect component for legacy stock routes
function StockRedirect() {
  const { symbol } = useParams<{ symbol: string }>();
  return <Navigate to={`/market?tab=stock&symbol=${symbol}`} replace />;
}

export function AppRoutes() {
  // Clear the chunk-reload flag on successful render so future deploys can retry
  React.useEffect(() => {
    sessionStorage.removeItem('chunk-reload');
  }, []);

  return (
    <Suspense fallback={<LoadingSpinner centered message="Loading..." />}>
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LandingPage />} />

        {/* Supabase callback - waits for auth to complete before redirecting */}
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><FeatureErrorBoundary featureName="Dashboard"><Dashboard /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/watchlist" element={<ProtectedRoute><FeatureErrorBoundary featureName="Watchlist"><Watchlist /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/market" element={<ProtectedRoute><FeatureErrorBoundary featureName="Market"><MarketMovers /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/stock/:symbol" element={<ProtectedRoute><StockRedirect /></ProtectedRoute>} />
        <Route path="/holdings" element={<ProtectedRoute><FeatureErrorBoundary featureName="Holdings"><Holdings /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><FeatureErrorBoundary featureName="Transactions"><Transactions /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/accounts" element={<ProtectedRoute><FeatureErrorBoundary featureName="Accounts"><Accounts /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/income" element={<ProtectedRoute><FeatureErrorBoundary featureName="Income"><Income /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/income-estimator" element={<ProtectedRoute><FeatureErrorBoundary featureName="Income Estimator"><IncomeEstimator /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/cost-basis" element={<ProtectedRoute><FeatureErrorBoundary featureName="Cost Basis"><CostBasis /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/gains" element={<ProtectedRoute><FeatureErrorBoundary featureName="Gains"><Gains /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute><FeatureErrorBoundary featureName="Performance"><Performance /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/rebalancer" element={<ProtectedRoute><FeatureErrorBoundary featureName="Rebalancer"><Rebalancer /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/options-lab" element={<ProtectedRoute><FeatureErrorBoundary featureName="Options Lab"><OptionsLab /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/import" element={<ProtectedRoute><FeatureErrorBoundary featureName="Import"><Import /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/taxes" element={<ProtectedRoute><FeatureErrorBoundary featureName="Taxes"><Taxes /></FeatureErrorBoundary></ProtectedRoute>} />
        <Route path="/settings/:section?" element={<ProtectedRoute><FeatureErrorBoundary featureName="Settings"><Settings /></FeatureErrorBoundary></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
